import { Text, Tooltip, Badge } from '@mantine/core';
import type { ColumnDef, Row } from '@tanstack/react-table';
import type { AttributeSchema } from '@ayocore/platform/common/helpers/schema-types';

/**
 * Генератор колонки по схеме атрибута.
 * Возвращает ColumnDef для указанного поля и типа строки TRow.
 * TODO: вынести генераторы в отдельные файлы
 */
export function generateColumnFromSchema<TRow extends Record<string, unknown>>(
  fieldName: string,
  fieldSchema: AttributeSchema
): ColumnDef<TRow> | null {
  const title = fieldSchema.title || fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
  const type = fieldSchema.type;

  const baseColumn: ColumnDef<TRow> = {
    id: fieldName,
    header: title,
    accessorKey: fieldName,
    enableSorting: ['string', 'number', 'integer', 'boolean'].includes(type),
  };

  if (fieldSchema.anyOf && Array.isArray(fieldSchema.anyOf)) {
    const nonNullType = fieldSchema.anyOf.find(t => (t as AttributeSchema).type !== 'null') as
      | AttributeSchema
      | undefined;
    if (nonNullType) {
      if (nonNullType.type === 'object')
        return generateObjectColumn(fieldName, nonNullType, baseColumn);
      switch (nonNullType.type) {
        case 'string':
          return generateStringColumn(fieldName, nonNullType, baseColumn);
        case 'number':
        case 'integer':
          return generateNumberColumn(fieldName, nonNullType, baseColumn);
        case 'boolean':
          return generateBooleanColumn(fieldName, nonNullType, baseColumn);
        case 'array':
          return generateArrayColumn(fieldName, nonNullType, baseColumn);
      }
    }
    return generateEnumColumn(fieldName, fieldSchema, baseColumn);
  }

  switch (type) {
    case 'string':
      return generateStringColumn(fieldName, fieldSchema, baseColumn);
    case 'number':
    case 'integer':
      return generateNumberColumn(fieldName, fieldSchema, baseColumn);
    case 'boolean':
      return generateBooleanColumn(fieldName, fieldSchema, baseColumn);
    case 'array':
      return generateArrayColumn(fieldName, fieldSchema, baseColumn);
    case 'object':
      return generateObjectColumn(fieldName, fieldSchema, baseColumn);
    default:
      return generateGenericColumn(fieldName, fieldSchema, baseColumn);
  }
}

export function generateStringColumn<TRow extends Record<string, unknown>>(
  fieldName: string,
  fieldSchema: AttributeSchema,
  baseColumn: ColumnDef<TRow>
): ColumnDef<TRow> {
  const maxLength = fieldSchema.$maxLength || 100;
  return {
    ...baseColumn,
    size: Math.min(Math.max(maxLength * 8, 150), 300),
    cell: ({ row }: { row: Row<TRow> }) => {
      const value = (row.original as Record<string, unknown>)[fieldName] as string | undefined;
      if (!value) return null;
      if (maxLength > 100) {
        return (
          <Tooltip label={value}>
            <Text size='xs' lineClamp={2}>
              {value}
            </Text>
          </Tooltip>
        );
      }
      return <Text size='xs'>{value}</Text>;
    },
  };
}

export function generateNumberColumn<TRow extends Record<string, unknown>>(
  fieldName: string,
  _fieldSchema: AttributeSchema,
  baseColumn: ColumnDef<TRow>
): ColumnDef<TRow> {
  return {
    ...baseColumn,
    size: 100,
    cell: ({ row }: { row: Row<TRow> }) => {
      const value = (row.original as Record<string, unknown>)[fieldName] as
        | number
        | string
        | undefined
        | null;
      if (value === null || value === undefined) return null;
      return (
        <Text size='xs' style={{ textAlign: 'right' }}>
          {Number(value).toLocaleString()}
        </Text>
      );
    },
  };
}

export function generateBooleanColumn<TRow extends Record<string, unknown>>(
  fieldName: string,
  _fieldSchema: AttributeSchema,
  baseColumn: ColumnDef<TRow>
): ColumnDef<TRow> {
  return {
    ...baseColumn,
    size: 80,
    cell: ({ row }: { row: Row<TRow> }) => {
      const value = (row.original as Record<string, unknown>)[fieldName] as boolean | undefined;
      return (
        <div style={{ textAlign: 'center' }}>
          {value ? (
            <Badge color='green' variant='light' size='xs'>
              ✓
            </Badge>
          ) : (
            <Badge color='gray' variant='light' size='xs'>
              ✗
            </Badge>
          )}
        </div>
      );
    },
  };
}

export function generateArrayColumn<TRow extends Record<string, unknown>>(
  fieldName: string,
  _fieldSchema: AttributeSchema,
  baseColumn: ColumnDef<TRow>
): ColumnDef<TRow> {
  return {
    ...baseColumn,
    size: 200,
    cell: ({ row }: { row: Row<TRow> }) => {
      const value = (row.original as Record<string, unknown>)[fieldName] as unknown[] | undefined;
      if (!Array.isArray(value) || value.length === 0) return null;
      return (
        <Tooltip label={value.join(', ')}>
          <Text size='xs' lineClamp={1}>
            {value.slice(0, 3).join(', ')}
            {value.length > 3 && '...'}
          </Text>
        </Tooltip>
      );
    },
  };
}

export function generateEnumColumn<TRow extends Record<string, unknown>>(
  fieldName: string,
  _fieldSchema: AttributeSchema,
  baseColumn: ColumnDef<TRow>
): ColumnDef<TRow> {
  return {
    ...baseColumn,
    size: 100,
    cell: ({ row }: { row: Row<TRow> }) => {
      const value = (row.original as Record<string, unknown>)[fieldName];
      if (!value) return null;
      return (
        <Badge variant='outline' size='xs'>
          {String(value).toUpperCase()}
        </Badge>
      );
    },
  };
}

export function generateObjectColumn<TRow extends Record<string, unknown>>(
  fieldName: string,
  fieldSchema: AttributeSchema,
  baseColumn: ColumnDef<TRow>
): ColumnDef<TRow> {
  return {
    ...baseColumn,
    size: 250,
    cell: ({ row }: { row: Row<TRow> }) => {
      const value = (row.original as Record<string, unknown>)[fieldName] as
        | Record<string, unknown>
        | null
        | undefined;
      if (value === null || value === undefined) return null;
      if (fieldSchema.properties && typeof value === 'object') {
        const entries = Object.entries(value)
          .filter(([_, val]) => val !== null && val !== undefined)
          .slice(0, 4)
          .map(([key, val]) => `${key}: ${val}`)
          .join(', ');
        return (
          <Tooltip label={JSON.stringify(value, null, 2)} multiline>
            <Text size='xs' lineClamp={2}>
              {entries}
              {Object.keys(value).length > 4 && '...'}
            </Text>
          </Tooltip>
        );
      }
      return (
        <Tooltip label={JSON.stringify(value, null, 2)} multiline>
          <Text size='xs' c='dimmed'>
            {Object.keys(value).length} fields
          </Text>
        </Tooltip>
      );
    },
  };
}

export function generateGenericColumn<TRow extends Record<string, unknown>>(
  fieldName: string,
  _fieldSchema: AttributeSchema,
  baseColumn: ColumnDef<TRow>
): ColumnDef<TRow> {
  return {
    ...baseColumn,
    size: 200,
    cell: ({ row }: { row: Row<TRow> }) => {
      const value = (row.original as Record<string, unknown>)[fieldName] as unknown;
      if (value === null || value === undefined) return null;
      if (typeof value === 'object' && !Array.isArray(value)) {
        const record = value as Record<string, unknown>;
        const keys = Object.keys(record);
        if (keys.length === 0) {
          return (
            <Text size='xs' c='dimmed'>
              Empty object
            </Text>
          );
        }
        const preview = keys
          .slice(0, 2)
          .map(key => `${key}: ${String(record[key])}`)
          .join(', ');
        return (
          <Tooltip label={JSON.stringify(value, null, 2)} multiline>
            <Text size='xs' lineClamp={1}>
              {preview}
              {keys.length > 2 && '...'}
            </Text>
          </Tooltip>
        );
      }
      if (Array.isArray(value)) {
        return (
          <Tooltip label={value.join(', ')}>
            <Text size='xs' lineClamp={1}>
              [{value.length} items]
            </Text>
          </Tooltip>
        );
      }
      return <Text size='xs'>{String(value)}</Text>;
    },
  };
}
